import { useState, useMemo } from "react";
import { FilterState, WhatsNewArticle } from "../types/whatsNew.types";
import { WhatsNewFilterService } from "../services/whatsNewFilter.service";
import { WHATS_NEW_CONSTANTS } from "../constants/whatsNew.constants";

export const useWhatsNewFilters = (articles: WhatsNewArticle[]) => {
  const [filters, setFilters] = useState<FilterState>({
    searchQuery: "",
    selectedModuleFilter: ["All"],
    selectedUpdateTypeFilter: "All",
    selectedDateFilter: "",
    selectedStatusFilter: "Active",
    customStartDate: "",
    customEndDate: "",
  });

  const filteredArticles = useMemo(() => {
    return WhatsNewFilterService.filterArticles(articles, filters);
  }, [articles, filters]);

  const updateSearchQuery = (searchQuery: string) => {
    setFilters((prev) => ({ ...prev, searchQuery }));
  };

  const updateModuleFilter = (moduleFilter: string) => {
    setFilters((prev) => {
      if (moduleFilter === "All") {
        return { ...prev, selectedModuleFilter: ["All"] };
      } else {
        const newSelection = prev.selectedModuleFilter.filter(
          (item) => item !== "All"
        );
        if (newSelection.includes(moduleFilter)) {
          const filtered = newSelection.filter((item) => item !== moduleFilter);
          return {
            ...prev,
            selectedModuleFilter: filtered.length === 0 ? ["All"] : filtered,
          };
        } else {
          return {
            ...prev,
            selectedModuleFilter: [...newSelection, moduleFilter],
          };
        }
      }
    });
  };

  const updateUpdateTypeFilter = (updateTypeFilter: string) => {
    setFilters((prev) => ({
      ...prev,
      selectedUpdateTypeFilter: updateTypeFilter,
    }));
  };

  const updateDateFilter = (dateFilter: string) => {
    setFilters((prev) => ({ ...prev, selectedDateFilter: dateFilter }));
  };

  const updateStatusFilter = (statusFilter: string) => {
    setFilters((prev) => ({ ...prev, selectedStatusFilter: statusFilter }));
  };

  const updateCustomDateRange = (startDate: string, endDate: string) => {
    setFilters((prev) => ({
      ...prev,
      customStartDate: startDate,
      customEndDate: endDate,
    }));
  };

  const clearAllFilters = () => {
    setFilters(WhatsNewFilterService.clearAllFilters());
  };

  const clearFilter = (filterType: keyof FilterState) => {
    setFilters((prev) => {
      switch (filterType) {
        case "searchQuery":
          return { ...prev, searchQuery: "" };
        case "selectedModuleFilter":
          return { ...prev, selectedModuleFilter: ["All"] };
        case "selectedUpdateTypeFilter":
          return { ...prev, selectedUpdateTypeFilter: "All" };
        case "selectedDateFilter":
          return { ...prev, selectedDateFilter: "" };
        case "selectedStatusFilter":
          return { ...prev, selectedStatusFilter: "" };
        default:
          return prev;
      }
    });
  };

  const hasActiveFilters = useMemo(() => {
    return !!(
      filters.searchQuery ||
      !filters.selectedModuleFilter.includes("All") ||
      filters.selectedUpdateTypeFilter !== "All" ||
      filters.selectedDateFilter ||
      filters.selectedStatusFilter
    );
  }, [filters]);

  const activeFiltersInfo = useMemo(() => {
    const activeFilters = [];

    if (filters.searchQuery) {
      activeFilters.push({
        type: "search" as const,
        label: `Search: "${filters.searchQuery}"`,
        onRemove: () => clearFilter("searchQuery"),
      });
    }

    if (!filters.selectedModuleFilter.includes("All")) {
      activeFilters.push({
        type: "module" as const,
        label: `Module: ${filters.selectedModuleFilter.join(", ")}`,
        onRemove: () => clearFilter("selectedModuleFilter"),
      });
    }

    if (filters.selectedUpdateTypeFilter !== "All") {
      activeFilters.push({
        type: "updateType" as const,
        label: `Type: ${filters.selectedUpdateTypeFilter}`,
        onRemove: () => clearFilter("selectedUpdateTypeFilter"),
      });
    }

    if (filters.selectedDateFilter) {
      activeFilters.push({
        type: "date" as const,
        label: `Date: ${filters.selectedDateFilter}`,
        onRemove: () => clearFilter("selectedDateFilter"),
      });
    }

    if (filters.selectedStatusFilter) {
      activeFilters.push({
        type: "status" as const,
        label: `Status: ${filters.selectedStatusFilter}`,
        onRemove: () => clearFilter("selectedStatusFilter"),
      });
    }

    return activeFilters;
  }, [filters]);

  return {
    filters,
    filteredArticles,
    hasActiveFilters,
    activeFiltersInfo,
    updateSearchQuery,
    updateModuleFilter,
    updateUpdateTypeFilter,
    updateDateFilter,
    updateStatusFilter,
    updateCustomDateRange,
    clearAllFilters,
    clearFilter,
    // Filter options
    moduleFilters: WHATS_NEW_CONSTANTS.FILTERS.MODULE_FILTERS,
    updateTypeFilters: WHATS_NEW_CONSTANTS.FILTERS.UPDATE_TYPE_FILTERS,
    dateFilters: WHATS_NEW_CONSTANTS.FILTERS.DATE_FILTERS,
    statusFilters: WHATS_NEW_CONSTANTS.FILTERS.STATUS_FILTERS,
  };
};
